import { useState, useRef, useEffect, type ChangeEvent, type KeyboardEvent } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { useTonConnectUI } from "@tonconnect/ui-react"

import { BaseAmountSheet } from "./BaseAmountSheet"

import { cn, formatBalance } from "@/shared/lib/utils"
import {
  getTonWithdrawalLimits,
  initiateWithdrawal,
} from "@/shared/api/endpoints"
import { useI18n } from "@/shared/i18n"
import { useFeatureFlag } from "@/shared/features"
import Button from "@/shared/ui/Button"
import { makeSuccessToast } from "@/shared/lib/toast-success"
import { makeErrorToast } from "@/shared/lib/toast-error"
import { telegram } from "@/shared/lib/telegram"
import type { UserGift } from "@/shared/api/types"
import { INVENTORY_GIFTS_PARAMS, useUserGifts } from "@/entities/gift"
import { getGiftRelayBotTelegramUrl } from "@/features/inventory/lib/relayBot"
import { GiftsGrid } from "@/features/inventory/ui/GiftsGrid"
import { GiftsEmptyHint } from "@/features/inventory/ui/GiftsEmptyHint"
import { GiftDetailsSheet } from "@/features/inventory/ui/GiftDetailsSheet"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WithdrawSheetProps {
  open: boolean
  onClose: () => void
  /** Formatted in-app balance string, e.g. "12.34" */
  balance: string
  /** Connected wallet address (raw) — also used as recipient_key */
  address: string
  /** On-chain wallet balance in TON, or null if unknown */
  walletBalance: number | null
  onDisconnect: () => void
  onSuccess?: () => void
}

// ─── Percent presets ──────────────────────────────────────────────────────────

const PCT_PRESETS = [
  { label: "25%", pct: 0.25 },
  { label: "50%", pct: 0.5 },
  { label: "75%", pct: 0.75 },
  { label: "100%", pct: 1 },
]

// ─── Component ────────────────────────────────────────────────────────────────

export const WithdrawSheet = ({
  open,
  onClose,
  balance,
  address,
  walletBalance,
  onDisconnect,
  onSuccess,
}: WithdrawSheetProps) => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const giftsEnabled = useFeatureFlag("gifts")
  const [tonConnectUI] = useTonConnectUI()
  const walletConnected = Boolean(address && address.length > 0)

  const [selectedTab, setSelectedTab] = useState<"TON" | "Gifts">("TON")
  const [inputValue, setInputValue] = useState("")
  const [selectedPct, setSelectedPct] = useState<number | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedGift, setSelectedGift] = useState<UserGift | null>(null)

  const amountInputRef = useRef<HTMLInputElement>(null)

  const isGiftsTab = giftsEnabled && selectedTab === "Gifts"
  const { data: giftsData } = useUserGifts(INVENTORY_GIFTS_PARAMS, {
    enabled: open && isGiftsTab,
  })
  const gifts = giftsData?.items ?? []

  // Fetch limits whenever the sheet opens
  const { data: limits } = useQuery({
    queryKey: ["tonWithdrawalLimits"],
    queryFn: getTonWithdrawalLimits,
    enabled: open && !!address,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  })

  // Derived values — all in TON
  // balanceTon — in-app balance (from prop, used for % presets)
  // remainingDayTon — how much is still withdrawable today (API limit cap)
  // maxPerDayTon — total daily limit ceiling (shown in label denominator)
  // minOnceTon — minimum single withdrawal
  const balanceTon = parseFloat(balance) || 0
  const remainingDayTon = limits ? limits.remaining_day / 1e9 : null
  const maxPerDayTon = limits ? limits.max_per_day / 1e9 : null
  const minOnceTon = limits ? limits.min_once / 1e9 : null

  // Raw amount for validation — never rounded, so comparison is always exact
  // % presets apply to the in-app balance; manual input is compared against remaining_day cap

  const effectiveAmount = (() => {
    if (selectedPct !== null) return balanceTon * selectedPct
    if (inputValue) return parseFloat(inputValue)
    return null
  })()

  const hasAmount =
    effectiveAmount !== null && !isNaN(effectiveAmount) && effectiveAmount > 0
  const belowMin =
    hasAmount && minOnceTon !== null && effectiveAmount! < minOnceTon
  const aboveMax =
    hasAmount && remainingDayTon !== null && effectiveAmount! > remainingDayTon
  const noAvailable = balanceTon <= 0
  const hasValidAmount = hasAmount && !belowMin && !aboveMax && !noAvailable

  // Display value: for presets show 2 decimals (cosmetic only), for manual input show as-is
  const inputDisplayValue =
    selectedPct !== null
      ? String(+(balanceTon * selectedPct).toFixed(2))
      : inputValue

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handlePctPreset = (pct: number) => {
    setSelectedPct(pct)
    setInputValue("")
    amountInputRef.current?.blur()
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const normalized = e.target.value.replace(",", ".").replace(/[^\d.]/g, "")
    const [intPartRaw, decimalPart] = normalized.split(".")
    // Strip leading zeros: "0007" → "7", keep "0" before decimal point
    const intPartTrimmed =
      intPartRaw.replace(/^0+(\d)/, "$1").slice(0, 6) ||
      (intPartRaw.length ? "0" : "")
    // Allow up to 2 decimal digits
    const hasDot = normalized.includes(".")
    const sanitized = hasDot
      ? `${intPartTrimmed}.${(decimalPart ?? "").slice(0, 2)}`
      : intPartTrimmed
    if (sanitized === "" || sanitized === "." || sanitized === "0.") {
      setInputValue(sanitized)
      setSelectedPct(null)
      return
    }
    if (isNaN(parseFloat(sanitized))) {
      setInputValue("")
      setSelectedPct(null)
      return
    }
    setInputValue(sanitized)
    setSelectedPct(null)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      amountInputRef.current?.blur()
    }
  }

  const handleWithdraw = async () => {
    if (!hasValidAmount || !address) return
    setIsProcessing(true)
    try {
      const amountNanotons = Math.round(effectiveAmount! * 1e9)
      await initiateWithdrawal({
        asset_class: "TON",
        recipient_key: address,
        amount: amountNanotons,
        nft_id: null,
      })

      trackAnalyticsEvent(AnalyticsEvent.WithdrawInitiated, {
        asset_class: "TON",
        amount_ton: effectiveAmount!,
      })
      makeSuccessToast({ label: t("withdraw.initiated") })

      setInputValue("")
      setSelectedPct(null)

      onSuccess?.()
      onClose()
    } catch (err) {
      if (err instanceof Error) {
        makeErrorToast({ label: err.message || t("withdraw.failedInitiate") })
      } else {
        makeErrorToast({ label: t("withdraw.failed") })
      }
    } finally {
      setIsProcessing(false)
    }
  }

  // ── Warning text ─────────────────────────────────────────────────────────────

  const amountWarning = (() => {
    if (belowMin && minOnceTon !== null)
      return t("withdraw.minWarning", { amount: minOnceTon })
    if (aboveMax && maxPerDayTon !== null)
      return t("withdraw.dayLimitWarning", { amount: maxPerDayTon.toFixed(2) })
    return null
  })()

  // ── Available to withdraw label ──────────────────────────────────────────────

  const availableLabel = (() => {
    if (remainingDayTon === null || maxPerDayTon === null) return null
    const avail = parseFloat(remainingDayTon.toFixed(2))
    const cap = parseFloat(maxPerDayTon.toFixed(0))
    return t("withdraw.availableToWithdraw", { available: avail, cap })
  })()

  const availableLabelIsWarning = noAvailable

  // ── Slots ────────────────────────────────────────────────────────────────────

  const extraInfo = availableLabel ? (
    <p
      className={cn(
        "text-center text-[14px] tracking-[-0.3px] transition-colors",
        availableLabelIsWarning ? "text-[#F97316]" : "text-white/40"
      )}
    >
      {availableLabel}
    </p>
  ) : null

  const presets = (
    <div className="grid grid-cols-4 gap-2">
      {PCT_PRESETS.map(({ label, pct }) => (
        <button
          key={label}
          onClick={() => handlePctPreset(pct)}
          disabled={isProcessing || noAvailable}
          className={cn(
            "h-[39px] rounded-full text-[16px] font-medium text-white transition-colors active:opacity-90 disabled:opacity-30",
            "bg-white/10"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )

  const ctaLabel = noAvailable
    ? t("common.notAvailable")
    : !hasAmount || belowMin || aboveMax
      ? t("common.enterAmount")
      : t("withdraw.action", { amount: effectiveAmount ?? "" })

  // No wallet: withdrawing TON needs a recipient — prompt to connect here
  // instead of blocking the button in Profile.
  const cta = walletConnected ? (
    <Button
      appearance="green"
      size="medium"
      isLoading={isProcessing}
      disabled={!hasValidAmount || isProcessing}
      onClick={handleWithdraw}
    >
      {ctaLabel}
    </Button>
  ) : (
    <Button
      appearance="green"
      size="medium"
      onClick={() => tonConnectUI.openModal()}
    >
      {t("common.connectWallet")}
    </Button>
  )

  // ── Currency tabs (TON / Gifts) ───────────────────────────────────────────────

  const openRelayBotChat = () => {
    telegram.openTelegramLink(getGiftRelayBotTelegramUrl())
  }

  const goToMarketplace = () => {
    onClose()
    navigate("/inventory?tab=marketplace")
  }

  useEffect(() => {
    if (!giftsEnabled && selectedTab === "Gifts") {
      setSelectedTab("TON")
    }
  }, [giftsEnabled, selectedTab])

  const tabs = giftsEnabled ? (
    <div className="mb-3 shrink-0 px-4">
      <div className="flex gap-0.5 rounded-full bg-[#7E7D9B]/[32%] p-1">
        {(["TON", "Gifts"] as const).map((tab) => {
          const active = selectedTab === tab
          return (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={cn(
                "h-8 flex-1 rounded-full text-sm/none font-medium transition-colors",
                active ? "bg-[#7E7D9B] text-white" : "text-white/70 active:text-white"
              )}
            >
              {tab}
            </button>
          )
        })}
      </div>
    </div>
  ) : null

  // ── Gifts tab: inventory-style grid (tap to withdraw) or empty hint ──────────

  const giftBody =
    gifts.length === 0 ? (
      <GiftsEmptyHint
        text={t("deposit.giftHint")}
        onHandleClick={openRelayBotChat}
      />
    ) : (
      <div className="h-full w-full overflow-y-auto">
        <GiftsGrid gifts={gifts} onSelect={setSelectedGift} />
      </div>
    )

  const giftCta = (
    <Button appearance="green" size="medium" onClick={goToMarketplace}>
      {t("withdraw.goToMarketplace")}
    </Button>
  )

  return (
    <>
      <BaseAmountSheet
        open={open}
        onClose={onClose}
        isProcessing={isProcessing}
        title={t("withdraw.title")}
        balance={formatBalance(balance)}
        address={isGiftsTab ? null : address}
        walletBalance={isGiftsTab ? null : walletBalance}
        onDisconnect={isGiftsTab ? undefined : onDisconnect}
        amountInputRef={amountInputRef}
        inputDisplayValue={inputDisplayValue}
        hasValidAmount={hasValidAmount}
        amountLabel={
          hasAmount && !belowMin && !aboveMax
            ? t("common.amount")
            : t("common.enterAmount")
        }
        amountWarning={amountWarning}
        amountIsWarning={!!(amountWarning && hasAmount)}
        onAmountChange={handleInputChange}
        onAmountKeyDown={handleKeyDown}
        topContent={tabs}
        body={isGiftsTab ? giftBody : undefined}
        extraInfo={isGiftsTab ? null : extraInfo}
        presets={isGiftsTab || !walletConnected ? null : presets}
        cta={isGiftsTab ? giftCta : cta}
      />

      <GiftDetailsSheet
        open={selectedGift !== null}
        onOpenChange={(o) => {
          if (!o) setSelectedGift(null)
        }}
        gift={selectedGift}
      />
    </>
  )
}
